import React, { useDispatch, useEffect } from "reactn"
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerCloseButton,
} from "@chakra-ui/modal"
import {
  Flex,
  Grid,
  GridItem,
  Spacer,
  useBreakpointValue
} from "@chakra-ui/react"
import ShoppingCart from "./shoppingCart"
import CartError from "./cartError"
import Checkout from "./checkout"
import { Box, Menu, Button } from "@chakra-ui/react"
import "./cartSidebar.css"
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  NotAllowedIcon,
} from "@chakra-ui/icons"
import { CartButton } from "../ui/cart-button"
import ShoppingCartFooter from "./shoppingCartFooter"
import ViewHeightComponent from "../utils/viewHeightComponent"
import { Dots } from "./dotsAnimation"
import Promo from "./sections/promo"
import CartUpsells from "./sections/cartUpsells"
import { useOnlineStatus } from "../utils/onlineStatus"
import Div100vh from "react-div-100vh"
import { API } from 'aws-amplify'
import * as Sentry from "@sentry/gatsby"


export function CartSidebar({ location, globalState }) {
  const { store, updateGlobal } = globalState
  const { cartOpen, checkoutOpen, itemsInCart, calculating, processing, expressCartData } = store
  const fetchCart = useDispatch("fetchCart")
  const isOnline = useOnlineStatus()

  // Prefetch express cart when cart opens (before checkout) to speed up checkout loading
  useEffect(() => {
    const prefetchExpressCart = async () => {
      // Only prefetch if:
      // 1. Cart is open
      // 2. We don't have express cart data yet
      // 3. We have cart products and session
      // 4. We have shipping address (backend handles defaults, but we need at least country)
      if (cartOpen && !expressCartData && store?.cartProducts?.length > 0 && store?.ss?.oid && store?.shipping_address?.country_code) {
        try {
          const items = store.cartProducts.map(item => ({
            ownGarment: item.own_garment,
            kvatt: item.kvatt,
            sku: item.sku,
            quantity: item.quantity,
          }));

          const cartData = {
            ss: store.ss,
            ec: true, // Express cart flag
            ca: {
              items: items,
              promo: store.promoCode?.promo_code || "",
              sa: {
                country_code: store.shipping_address.country_code,
                zip: store.shipping_address.zip,
                address1: store.shipping_address.address1 || '',
                address2: store.shipping_address.address2 || '',
                city: store.shipping_address.city,
                locality: store.shipping_address.locality,
                name: store.fullName || '',
                phone: store.contactNumber || '',
                complete: true
              },
              sp: {
                shippingID: store.shippingID,
                packagingID: store.packagingID
              },
              ...(store.emailAddress ? { cd: { email: store.emailAddress } } : {})
            },
          };

          const response = await API.post('api', '/checkout/cart', {
            body: cartData
          });

          if (response?.ss && response?.expressPd?.cs) {
            updateGlobal({ expressCartData: response });
            console.log('[EXPRESS CART] Prefetched when cart opened:', {
              hasExpressPd: !!response?.expressPd?.cs,
              expressTotalPrice: response?.ts?.total_price,
              expressTotalPricePence: response?.ts?.total_price_pence
            });
          }
        } catch (error) {
          console.error('[EXPRESS CART] Error prefetching express cart:', error);
          // Capture error in Sentry
          if (typeof window !== 'undefined' && typeof Sentry !== 'undefined') {
            Sentry.captureException(error, {
              tags: {
                source: 'expressCartPrefetch',
                component: 'cartSidebar'
              },
              extra: {
                endpoint: '/checkout/cart',
                errorMessage: error?.message,
                errorStack: error?.stack
              },
              level: 'error'
            });
          }
          // Don't block cart opening if prefetch fails
        }
      }
    };

    prefetchExpressCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartOpen, store?.cartProducts?.length, store?.ss?.oid, store?.shipping_address?.country_code]); // Prefetch when cart opens

  // // Fetch cart data when cart is opened (if not already initialised)
  // React.useEffect(() => {
  //   if (cartOpen && !store.cartInitialised) {
  //     fetchCart()
  //   }
  // }, [cartOpen])

  const splitView = useBreakpointValue([false, false, true, true])
  const size = useBreakpointValue(["full", "full", "md", "md"])
  const sizeOpen = useBreakpointValue(["full", "full", "full", "full"])
  const gridColumns = useBreakpointValue([4, 4, 4, 4, 5])
  const gridSpan = useBreakpointValue([2, 2, 2, 2, 3])

  // Show test button in development/staging only
  const isDevOrStaging = process.env.GATSBY_ACTIVE_ENV === 'development' ||
                         process.env.GATSBY_ACTIVE_ENV === 'staging' ||
                         process.env.NODE_ENV === 'development'

  const testSentryError = () => {
    console.log('[SENTRY TEST] Triggering test error...');
    console.log('[SENTRY TEST] Sentry available?', typeof Sentry !== 'undefined', Sentry);

    if (typeof Sentry === 'undefined' || !Sentry.captureException) {
      console.error('[SENTRY TEST] Sentry is not available! Check initialization.');
      return;
    }

    try {
      const testError = new Error(`Sentry Test Error from Cart - ${new Date().toISOString()}`);
      console.log('[SENTRY TEST] Error created:', testError);

      const eventId = Sentry.captureException(testError, {
        tags: {
          source: 'cart_test',
          test: 'true',
          component: 'cartSidebar'
        },
        extra: {
          test: true,
          timestamp: Date.now(),
          cartOpen: cartOpen,
          checkoutOpen: checkoutOpen,
          itemsInCart: itemsInCart
        },
        level: 'error'
      });

      console.log('[SENTRY TEST] Test error sent to Sentry, event ID:', eventId);
      console.log('[SENTRY TEST] Check network tab for Sentry requests');
    } catch (error) {
      console.error('[SENTRY TEST] Error calling Sentry.captureException:', error);
    }
  }

  return (
    <>
      <Drawer
        variant={"secondary"}
        placement={"right"}
        onClose={() => {
          updateGlobal({ cartOpen: false, checkoutOpen: false })
        }}
        isOpen={cartOpen}
        size={checkoutOpen ? sizeOpen : size}
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <Div100vh>
        <DrawerContent backgroundColor={"white"}>
          <DrawerHeader backgroundColor={"black"} pt={1} minHeight={"56px"}>
            <DrawerCloseButton size={"lg"}mt={0}_focus={{boxShadow: "none"}}color={"white"}border={"none"}outline={"none"}/>
            <Flex>
              <Box color={"white"} mt={2} fontSize={20} fontWeight={400}>
                CHECKOUT
              </Box>
              <Spacer />
              {isDevOrStaging && (
                <Box mt={1} mr={2}>
                  <Button
                    size="xs"
                    colorScheme="red"
                    onClick={testSentryError}
                    title="Test Sentry Error"
                  >
                    🐛 Test Sentry
                  </Button>
                </Box>
              )}
              <Box mt={1} mr={4}>
                {checkoutOpen && <CartButton globalState={globalState} />}
              </Box>
            </Flex>
          </DrawerHeader>

          <DrawerBody fontSize={18}
            minHeight={"120dvh !important"}
            height={"120dvh !important"}
            backgroundColor={"white !important"}
            style={{height: '120dvh'}}
          >
             <Box maxH="80vh" >
            <Menu isOpen={cartOpen}id={"cartsidebar"}itemListClassName={"cart-list"}rightpageWrapId={"page-wrap-cart"}outerContainerId={"outer-container-cart"}>
            <Flex height={"100%"} direction={"row"}>
              <Box id="page-content" minWidth={"100%"} >
                <Grid id="grid"templateColumns={`repeat(${checkoutOpen ? (splitView ? gridColumns : 1) : 1}, 1fr)`}gap={splitView ? "20px" : "0px"}>
                  {(!checkoutOpen || (checkoutOpen && splitView)) && (
                    <GridItem maxW="90vw" id="gi1" colSpan={splitView ? 2 : 1}>
                      {itemsInCart ? (
                          <ViewHeightComponent viewHeight={85}>
                          <Box id="shoppingCartx" height={["80vh","80vh","90vh","90vh"]} >
                            <Flex maxHeight={"100%"} minHeight={"100%"} direction={"column"}>
                              <Box flex="1" overflowY={["scroll","scroll","scroll","scroll",]}>
                                <ShoppingCart showSummary={checkoutOpen}globalState={globalState}/>
                              </Box>
                              {!checkoutOpen && (
                                <Box width="95%" maxW={'600px'} >
                                  <CartUpsells globalState={globalState} />
                                  <Promo globalState={globalState} />
                                </Box>
                              )}
                              <Box>
                                {checkoutOpen ? (
                                  <ShoppingCartFooter globalState={globalState}/>
                                ) : (
                                  <>
                                    <CartError />
                                    <Button
                                      mb={[5, 5, 0, 0]}
                                      type={"button"}
                                      variant={"checkout"}
                                      isDisabled={!isOnline || processing || calculating}
                                      rightIcon={calculating ? (<Dots mt={2} size="xs" />
                                      ) : isOnline ? (
                                        <ArrowForwardIcon />
                                      ) : (
                                        <NotAllowedIcon />
                                      )}
                                      onClick={(e) => {
                                        // iOS touch handling - prevent double tap
                                        if (e) {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                        updateGlobal({ checkoutOpen: true })
                                        // Cart will be fetched automatically by useEffect when checkoutOpen becomes true
                                      }}
                                      onTouchStart={(e) => {
                                        // iOS touch handling - prevent double tap
                                        e.stopPropagation()
                                      }}
                                      style={{
                                        WebkitTapHighlightColor: 'transparent',
                                        touchAction: 'manipulation',
                                        userSelect: 'none'
                                      }}
                                    >
                                      {calculating ? "" : "CHECKOUT"}
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </Flex>
                          </Box>
                        </ViewHeightComponent>
                      ) : (
                        <ViewHeightComponent viewHeight={85}>
                          <Flex direction={"column"} height={"100%"}>
                            <Box textAlign={"center"} mt={20} flex="1">
                              <Box>YOUR CART IS EMPTY</Box>
                            </Box>
                            <Box>
                              <Button variant="checkout"fontSize={14}leftIcon={<ArrowBackIcon />}
                                onClick={() => updateGlobal({ cartOpen: false })}
                              >
                                CONTINUE SHOPPING
                              </Button>
                            </Box>
                          </Flex>
                        </ViewHeightComponent>
                      )}
                    </GridItem>
                  )}

                  {checkoutOpen && itemsInCart && (
                    <GridItem id="gic"maxW="90vw"colSpan={splitView ? gridSpan : 1}>
                      <ViewHeightComponent viewHeight={85}>
                        <Box id="Checkout" height={["80vh","80vh","80vh","80vh"]} overflowY={['scroll','scroll','unset','unset']}>
                          <Checkout location={location}globalState={globalState}/>
                        </Box>
                      </ViewHeightComponent>
                    </GridItem>
                  )}
                </Grid>
              </Box>
              </Flex>
            </Menu>
            </Box>
          </DrawerBody>
        </DrawerContent>
        </Div100vh>
      </Drawer>
    </>
  )
}